import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  makeStyles
} from "@material-ui/core";
import Modal from "@/components/Modal";
import { useTranslation } from "@/langs/useTranslation";
import { SelectedScheduleTemplateDatatable } from "@/helpers/SelectedScheduleTemplateDatatable";
import { EnabledScheduleTemplateListDatatable } from "@/helpers/EnabledScheduleTemplateListDatatable";
import {
  bindDevicesScheduleTemplatesApi,
  unbindDevicesScheduleTemplatesApi
} from "@/apis/apiCollection";
import { toast } from "@/apis/request";
import MemoedHOC from "@/hoc/MemoedHOC";
import { useGroupDeviceState } from "./Provider";
import { useUpdateEffect } from "ahooks";
import flatten from "lodash/flatten";

const useStyles = makeStyles(theme => ({
  item: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    display: "flex"
  },
  label: {
    marginRight: theme.spacing(1),
    fontWeight: "bold"
  },
  content: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    background: "#F8F9FC",
    borderRadius: 10,
    marginTop: theme.spacing(2)
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),

    "& .MuiButton-root": {
      marginRight: theme.spacing(2),
      width: 120
    }
  }
}));

function SetScheduleModal({ getSelectedDevices, crossSelect, open, onClose }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const tableRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [type, setType] = useState(1);
  const [selectTemplateOpen, setSelectTemplateOpen] = useState(false);
  const [tableData, setTableData] = useState([]);

  let { currentGroup, currentTag } = useGroupDeviceState();

  if (!currentGroup) {
    currentGroup = {};
  }

  const checkedGroups = currentGroup.checkedGroups || [];
  const tagId = !crossSelect ? undefined : currentTag?.id;

  const groupIds = useMemo(() => {
    if (!crossSelect) return [];
    return checkedGroups.length > 0
      ? checkedGroups.filter(group => !group.isRoot).map(group => group.id)
      : currentGroup.id && !currentGroup.isRoot
      ? [currentGroup.id]
      : [];
  }, [checkedGroups.length, currentGroup.id, crossSelect]);

  const getSelectedRows = () => {
    const selectedIds = tableRef.current.getSelected();
    const data = tableRef.current?.api().data().toArray();
    const selectedRows = flatten(
      selectedIds.map(id =>
        data.filter(item => item.schedule_template_id === id)
      )
    );
    return selectedRows;
  };

  const getDevices = () => {
    return devices;
  };

  useEffect(() => {
    if (open) {
      setDevices(getSelectedDevices());
    }
  }, [open]);

  const handleAddTemplate = () => {
    setSelectTemplateOpen(true);
    setType(1);
  };

  const handleDelete = row => {
    setTableData(prevList =>
      prevList.filter(
        item => item.schedule_template_id !== row.schedule_template_id
      )
    );
  };

  const setBindDevice = (deviceData, rowIndex) => {
    setTableData(prevList =>
      prevList.map((item, index) => {
        if (index === rowIndex) {
          return {
            ...item,
            deviceData
          };
        }
        return item;
      })
    );
  };

  const handleSelectConfirm = () => {
    const templates = getSelectedRows();
    if (!templates.length) {
      return toast.info(t({ defaultMessage: "未选择任何模板" }));
    }
    setTableData(prev => [...prev, ...templates]);
    setSelectTemplateOpen(false);
  };

  const handleConfirm = async () => {
    const group_ids = groupIds.length ? groupIds : undefined;
    const tag_ids = tagId ? [tagId] : undefined;
    const device_ids = devices.map(item => item.id);
    const schedule_template_ids = [
      ...new Set(tableData.map(item => item.schedule_template_id))
    ];
    const power_on_device_ids = [
      ...new Set(tableData.map(item => item.deviceData?.id))
    ].filter(Boolean);
    const targetType = group_ids ? 1 : tag_ids ? 2 : 0; // 0设备 1门店 2标签
    const taskType =
      targetType === 1 ? "group" : targetType === 2 ? "tag" : "device";
    if (
      tableData.some(item => item.task_type === "start" && !item.deviceData)
    ) {
      return toast.info(t({ defaultMessage: "开机任务需要指定一个开机设备" }));
    }
    if (type === 1) {
      await bindDevicesScheduleTemplatesApi({
        type: taskType,
        targetType,
        group_ids,
        tag_ids,
        device_ids: targetType === 0 ? device_ids : undefined,
        schedule_template_ids,
        power_on_device_ids
      });
    } else {
      await unbindDevicesScheduleTemplatesApi({
        type: taskType,
        targetType,
        group_ids,
        tag_ids,
        device_ids: targetType === 0 ? device_ids : undefined,
        schedule_template_ids, // 选填, 如果不传, 则删除这个设备下的所有模板
        power_on_device_ids
      });
    }
    onClose?.();
    toast.success(t({ defaultMessage: "操作成功" }));
  };

  const getDesc = () => {
    if (crossSelect) {
      if (currentGroup.id) {
        if (currentGroup.id === "all") {
          return t({ defaultMessage: "移除所有本设备套用的排程模版" });
        }
        return t({ defaultMessage: "移除所有本门店套用的排程模版" });
      }
      if (currentTag) {
        return t({ defaultMessage: "移除所有本标签套用的排程模版" });
      }
    }
    return t({ defaultMessage: "移除所有本设备套用的排程模版" });
  };

  useUpdateEffect(() => {
    if (!open) {
      setTableData([]);
    }
  }, [open]);

  return (
    <>
      <Modal
        title={t({ defaultMessage: "设置排程" })}
        width={1000}
        open={open}
        onClose={onClose}
        onConfirm={handleConfirm}
      >
        <RadioGroup value={type} onChange={e => setType(+e.target.value)}>
          <div>
            <FormControlLabel
              value={1}
              control={<Radio color="primary" />}
              label={
                <Typography variant="body1" className={classes.label}>
                  {t({ defaultMessage: "套用排程模板" })}
                </Typography>
              }
            />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ display: "inline-block" }}
            >
              {t({
                defaultMessage:
                  "若后续排程模板的内容有更新，也会同步给套用的设备"
              })}
            </Typography>
            <div style={{ width: "100%", marginBottom: 30, paddingLeft: 29 }}>
              <Button
                color="primary"
                variant="outlined"
                onClick={handleAddTemplate}
              >
                {t({ defaultMessage: "选择排程模板" })}
              </Button>
              <SelectedScheduleTemplateDatatable
                data={tableData}
                onDelete={handleDelete}
                setDeviceData={setBindDevice}
                getDevices={getDevices}
                style={{ marginTop: 24 }}
              />
            </div>
          </div>

          <div>
            <FormControlLabel
              value={2}
              control={<Radio color="primary" />}
              label={
                <Typography variant="body1" className={classes.label}>
                  {getDesc()}
                </Typography>
              }
            />
          </div>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ paddingLeft: 29 }}
          >
            {t({
              defaultMessage: "移除设备目前所套用的排程模板，不再执行"
            })}
          </Typography>
        </RadioGroup>
      </Modal>
      <Modal
        title={t({ defaultMessage: "选择排程模板" })}
        width={1000}
        open={selectTemplateOpen}
        onClose={() => {
          setSelectTemplateOpen(false);
        }}
        onConfirm={handleSelectConfirm}
      >
        <EnabledScheduleTemplateListDatatable
          ref={tableRef}
          getDevices={getDevices}
        />
      </Modal>
    </>
  );
}

export default MemoedHOC(SetScheduleModal);
